<script setup lang="ts">
import type { LearningActivity } from '~/composables/useCourse';
import { CourseContentActivityType } from '~/enums/course.enums';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps<{
  completed?: boolean;
  active?: boolean;
  passed?: boolean;
  activityType: LearningActivity['type'];
  locked?: boolean;
}>();

const quizTypes = [CourseContentActivityType.Quiz, CourseContentActivityType.KickoffQuiz, CourseContentActivityType.MasteryQuiz];
const contentTypes = [
  CourseContentActivityType.Lesson,
  CourseContentActivityType.Quiz,
  CourseContentActivityType.KickoffQuiz,
  CourseContentActivityType.MasteryQuiz,
  CourseContentActivityType.Case,
  CourseContentActivityType.Comparison,
];

const icon = computed(() => {
  if (props.locked) {
    return 'fa-lock';
  }

  if (props.completed) {
    if (quizTypes.includes(props.activityType as CourseContentActivityType)) {
      return 'fa-bolt-lightning';
    }

    if (props.activityType === CourseContentActivityType.Lesson) {
      return 'fa-check';
    }
  }

  if (contentTypes.includes(props.activityType as CourseContentActivityType)) {
    switch (props.activityType) {
      case CourseContentActivityType.Lesson:
        return 'fa-circle-play';

      case CourseContentActivityType.Quiz:
      case CourseContentActivityType.KickoffQuiz:
      case CourseContentActivityType.MasteryQuiz:
        return 'fa-bolt-lightning';

      case CourseContentActivityType.Case:
        return 'fa-clipboard-list-check';

      case CourseContentActivityType.Comparison:
        return 'fa-chart-simple';
    }
  }

  return 'fa-circle';
});

const classes = computed(() => {
  return {
    'is-completed': props.completed,
    'is-active': props.active,
    'is-locked': props.locked,
    'is-failed': quizTypes.includes(props.activityType as CourseContentActivityType) && props.completed && !props.passed,
  };
});
</script>

<template>
  <FontAwesomeIcon
    v-if="icon.includes('circle')"
    class="!w-6 !h-6 activity-status-icon--circle bg-white rounded-full"
    :icon="icon"
    :class="classes"
  />

  <div
    v-else
    class="activity-status-icon w-6 h-6 rounded-full flex justify-center items-center"
    :class="classes"
  >
    <FontAwesomeIcon
      class="text-12 w-3"
      :icon="icon"
    />
  </div>
</template>

<style lang="scss">
.activity-status-icon {
  background: var(--palette-neutral-40);
  color: white;

  &.is-completed {
    background: var(--palette-green-60);
  }

  &.is-active {
    background: var(--palette-neutral-100);
  }

  &.is-locked {
    background: var(--palette-neutral-20);
    color: var(--palette-neutral-60);
  }

  &.is-failed {
    background: var(--palette-red-60);
  }
}

.activity-status-icon--circle {
  color: var(--palette-neutral-40);

  &.is-completed {
    color: var(--palette-green-40);
  }

  &.is-active {
    color: var(--palette-neutral-100);
  }

  &.is-locked {
    color: var(--palette-neutral-20);
    background: var(--palette-neutral-60);
  }

  &.is-failed {
    color: var(--palette-red-40);
  }
}
</style>